import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  Heart,
  HeartHandshake,
  GraduationCap,
  Check,
  MessageCircle,
  Shield,
  Sparkles,
} from "lucide-react";
import SectionEyebrow from "@/components/SectionEyebrow";
import { IMAGES, CONTACT } from "@/data/content";
import api from "@/lib/api";

const CAUSES = [
  {
    key: "Missions d'Évangélisation",
    title: "Missions d'Évangélisation",
    desc: "Financez l'envoi de missionnaires et les campagnes d'évangélisation.",
    icon: Heart,
  },
  {
    key: "Actions Sociales",
    title: "Actions Sociales",
    desc: "Soutenez les actions médicales, l'aide alimentaire et le développement.",
    icon: HeartHandshake,
  },
  {
    key: "Formation des Leaders",
    title: "Formation des Leaders",
    desc: "Investissez dans la formation de la prochaine génération de leaders.",
    icon: GraduationCap,
  },
];

const AMOUNTS = [10, 25, 50, 100, 250];

// Branded payment method cards
const PAYMENT_METHODS = [
  {
    key: "Zelle",
    name: "Zelle",
    brandColor: "#6D1ED4",
    logo: (
      <div className="flex items-baseline gap-1">
        <span
          className="font-heading font-black text-4xl tracking-tight"
          style={{ color: "#6D1ED4" }}
        >
          Zelle
        </span>
        <span
          className="text-2xl leading-none"
          style={{ color: "#6D1ED4" }}
        >
          ®
        </span>
      </div>
    ),
    tagline: "Transfert bancaire US",
  },
  {
    key: "MonCash",
    name: "MonCash",
    brandColor: "#E30613",
    logo: (
      <div className="flex items-center gap-2">
        <div
          className="h-10 w-10 rounded-full flex items-center justify-center font-heading font-black text-white text-lg"
          style={{ background: "#E30613" }}
        >
          M
        </div>
        <span
          className="font-heading font-black text-3xl tracking-tight"
          style={{ color: "#E30613" }}
        >
          MonCash
        </span>
      </div>
    ),
    tagline: "Mobile Money · Haïti",
  },
  {
    key: "NatCash",
    name: "NatCash",
    brandColor: "#009B48",
    logo: (
      <div className="flex items-center gap-2">
        <div
          className="h-10 w-10 rounded-full flex items-center justify-center"
          style={{ background: "#009B48" }}
        >
          <span className="text-white font-heading font-black text-lg">N</span>
        </div>
        <span
          className="font-heading font-black text-3xl tracking-tight"
          style={{ color: "#009B48" }}
        >
          NatCash
        </span>
      </div>
    ),
    tagline: "Mobile Money · Natcom",
  },
];

const Donation = () => {
  const [cause, setCause] = useState(CAUSES[0].key);
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [method, setMethod] = useState("Zelle");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "Haïti",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const finalAmount = () => {
    const amt = custom ? parseFloat(custom) : amount;
    return isFinite(amt) && amt > 0 ? amt : 0;
  };

  const buildWhatsAppUrl = (amt) => {
    // MIREVSA WhatsApp — strip non-digits for wa.me
    const whatsappNumber = CONTACT.whatsapp.replace(/[^0-9]/g, "");
    const lines = [
      "Bonjour MIREVSA,",
      "Je souhaite effectuer un don.",
      "",
      `Cause : ${cause}`,
      `Montant : $${amt} USD`,
      `Nom : ${form.name}`,
      `Email : ${form.email}`,
      `Téléphone : ${form.phone}`,
      `Pays : ${form.country || "Haïti"}`,
      `Méthode de paiement choisie : ${method}`,
    ];
    if (form.message && form.message.trim()) {
      lines.push("", `Message : ${form.message.trim()}`);
    }
    lines.push("", "Merci de m'envoyer les instructions pour finaliser mon don.");
    const message = encodeURIComponent(lines.join("\n"));
    return `https://wa.me/${whatsappNumber}?text=${message}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    const amt = finalAmount();
    if (!amt) {
      toast.error("Veuillez saisir un montant valide.");
      return;
    }
    setSubmitting(true);
    // Save donation intent (best-effort, non-blocking for UX)
    try {
      await api.post("/donation-intent", {
        ...form,
        cause,
        amount: amt,
        message: `[Méthode: ${method}] ${form.message || ""}`.trim(),
      });
    } catch {
      // Silent — user can still contact via WhatsApp
    }
    // Open WhatsApp in a new tab with pre-filled message
    const url = buildWhatsAppUrl(amt);
    window.open(url, "_blank", "noopener,noreferrer");
    setConfirmed(true);
    setSubmitting(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => {
    setConfirmed(false);
    setCause(CAUSES[0].key);
    setAmount(50);
    setCustom("");
    setMethod("Zelle");
    setForm({ name: "", email: "", phone: "", country: "Haïti", message: "" });
  };

  return (
    <div data-testid="donation-page">
      {/* HERO */}
      <section className="relative min-h-[55vh] flex items-end overflow-hidden">
        <img src={IMAGES.footerImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B3D91]/90 via-[#0B3D91]/60 to-[#0B3D91]/30" />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative mv-container py-24 text-white">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-white/80" />
            <span className="text-xs uppercase tracking-[0.3em] font-bold">Faire un don</span>
          </div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Soutenez la mission.
          </h1>
          <p className="mt-6 text-lg text-blue-50/95 max-w-2xl">
            Votre soutien permet de financer les missions, les actions sociales et la formation des leaders.
            Chaque contribution participe à transformer des vies.
          </p>
        </div>
      </section>

      {/* CONFIRMATION STATE */}
      {confirmed ? (
        <section className="mv-section bg-[#F8FAFC]" data-testid="donation-confirmed">
          <div className="mv-container max-w-2xl">
            <div className="bg-white p-10 md:p-14 rounded-2xl border border-gray-200 text-center">
              <div className="mx-auto h-20 w-20 rounded-full bg-[#0B3D91]/10 flex items-center justify-center">
                <Sparkles size={32} className="text-[#0B3D91]" />
              </div>
              <h2 className="mt-6 font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1C1C1C]">
                Merci pour votre générosité !
              </h2>
              <p className="mt-4 text-base md:text-lg text-muted leading-relaxed">
                Notre équipe vous contactera rapidement sur WhatsApp afin de finaliser
                votre don en toute sécurité.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0B3D91]/5 text-[#0B3D91] text-sm font-bold">
                <MessageCircle size={16} /> WhatsApp ouvert dans un nouvel onglet
              </div>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <a
                  href={buildWhatsAppUrl(finalAmount() || amount)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary !py-3 !px-6 rounded-full"
                  data-testid="reopen-whatsapp"
                >
                  Rouvrir WhatsApp <MessageCircle size={18} />
                </a>
                <button
                  onClick={reset}
                  className="btn-outline !py-3 !px-6 rounded-full"
                  data-testid="new-donation"
                >
                  Faire un autre don
                </button>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* CAUSES */}
          <section className="mv-section bg-white" data-testid="causes-section">
            <div className="mv-container">
              <div className="max-w-2xl mb-10">
                <SectionEyebrow>Étape 1 · Choisir votre cause</SectionEyebrow>
                <h2 className="mt-5 font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1C1C1C]">
                  Où voulez-vous voir un impact ?
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {CAUSES.map((c) => {
                  const Icon = c.icon;
                  const active = cause === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setCause(c.key)}
                      data-testid={`cause-${c.key}`}
                      className={`text-left p-7 rounded-2xl border-2 transition-all duration-300 ${
                        active
                          ? "border-[#0B3D91] bg-[#0B3D91] text-white shadow-lg"
                          : "border-gray-200 bg-white hover:border-[#0B3D91]/50 hover:shadow-md"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div
                          className={`h-12 w-12 rounded-xl flex items-center justify-center ${
                            active ? "bg-white/15" : "bg-[#0B3D91]/10"
                          }`}
                        >
                          <Icon size={22} className={active ? "text-white" : "text-[#0B3D91]"} />
                        </div>
                        {active && (
                          <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center">
                            <Check size={14} className="text-white" />
                          </div>
                        )}
                      </div>
                      <h3 className={`mt-5 font-heading font-bold text-xl ${active ? "text-white" : "text-[#1C1C1C]"}`}>
                        {c.title}
                      </h3>
                      <p className={`mt-2 text-sm leading-relaxed ${active ? "text-blue-100" : "text-muted"}`}>
                        {c.desc}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {/* AMOUNT + FORM + PAYMENT */}
          <section className="pb-24 bg-[#F8FAFC]" data-testid="donation-form-section">
            <div className="mv-container">
              <form onSubmit={submit} className="bg-white p-8 md:p-12 rounded-2xl border border-gray-200 shadow-sm" data-testid="donation-form">
                {/* Amount */}
                <div>
                  <SectionEyebrow>Étape 2 · Montant du don</SectionEyebrow>
                  <div className="mt-5 grid grid-cols-3 sm:grid-cols-5 gap-3">
                    {AMOUNTS.map((a) => (
                      <button
                        key={a}
                        type="button"
                        onClick={() => {
                          setAmount(a);
                          setCustom("");
                        }}
                        data-testid={`amount-${a}`}
                        className={`py-4 font-bold text-lg rounded-xl border-2 transition-all ${
                          amount === a && !custom
                            ? "bg-[#0B3D91] text-white border-[#0B3D91] shadow-md scale-[1.02]"
                            : "bg-white text-[#1C1C1C] border-gray-300 hover:border-[#0B3D91]"
                        }`}
                      >
                        ${a}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <span className="text-sm font-bold text-muted">Autre montant :</span>
                    <div className="relative flex-1 max-w-xs">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-[#1C1C1C]">
                        $
                      </span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        placeholder="Montant personnalisé"
                        value={custom}
                        onChange={(e) => setCustom(e.target.value)}
                        data-testid="amount-custom"
                        className="w-full border-2 border-gray-300 pl-8 pr-4 py-3 rounded-xl focus:outline-none focus:border-[#0B3D91] transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="mt-12">
                  <SectionEyebrow>Étape 3 · Vos informations</SectionEyebrow>
                  <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-widest font-bold text-[#1C1C1C]">
                        Nom complet <span className="text-red-500">*</span>
                      </label>
                      <input
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="donate-name"
                        className="mt-2 w-full border-2 border-gray-300 px-4 py-3 rounded-xl focus:outline-none focus:border-[#0B3D91] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest font-bold text-[#1C1C1C]">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="donate-email"
                        className="mt-2 w-full border-2 border-gray-300 px-4 py-3 rounded-xl focus:outline-none focus:border-[#0B3D91] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest font-bold text-[#1C1C1C]">
                        Téléphone / WhatsApp <span className="text-red-500">*</span>
                      </label>
                      <input
                        required
                        type="tel"
                        placeholder="+509..."
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        data-testid="donate-phone"
                        className="mt-2 w-full border-2 border-gray-300 px-4 py-3 rounded-xl focus:outline-none focus:border-[#0B3D91] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest font-bold text-[#1C1C1C]">
                        Pays
                      </label>
                      <input
                        value={form.country}
                        onChange={(e) => setForm({ ...form, country: e.target.value })}
                        data-testid="donate-country"
                        className="mt-2 w-full border-2 border-gray-300 px-4 py-3 rounded-xl focus:outline-none focus:border-[#0B3D91] transition-colors"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-widest font-bold text-[#1C1C1C]">
                        Message (facultatif)
                      </label>
                      <textarea
                        rows={3}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        data-testid="donate-message"
                        className="mt-2 w-full border-2 border-gray-300 px-4 py-3 rounded-xl focus:outline-none focus:border-[#0B3D91] transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="mt-12">
                  <SectionEyebrow>Étape 4 · Méthode de paiement</SectionEyebrow>
                  <p className="mt-3 text-sm text-muted max-w-xl">
                    Choisissez votre moyen de paiement — notre équipe vous enverra les instructions détaillées via WhatsApp.
                  </p>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {PAYMENT_METHODS.map((m) => {
                      const active = method === m.key;
                      return (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => setMethod(m.key)}
                          data-testid={`method-${m.key}`}
                          className={`group relative overflow-hidden p-7 rounded-2xl border-2 bg-white transition-all duration-300 ${
                            active
                              ? "border-[#0B3D91] shadow-xl -translate-y-1"
                              : "border-gray-200 hover:border-gray-400 hover:shadow-md"
                          }`}
                          style={active ? { boxShadow: `0 10px 25px -8px ${m.brandColor}40` } : {}}
                        >
                          {/* Brand color accent bar on hover / active */}
                          <div
                            className={`absolute top-0 inset-x-0 h-1 transition-all duration-300 ${
                              active ? "opacity-100" : "opacity-0 group-hover:opacity-70"
                            }`}
                            style={{ background: m.brandColor }}
                          />
                          {/* Selected badge */}
                          {active && (
                            <div className="absolute top-4 right-4 h-7 w-7 rounded-full bg-[#0B3D91] flex items-center justify-center">
                              <Check size={14} className="text-white" />
                            </div>
                          )}
                          {/* Logo */}
                          <div className="flex items-center justify-center min-h-[64px]">
                            {m.logo}
                          </div>
                          {/* Tagline */}
                          <div className="mt-4 text-center text-xs uppercase tracking-widest font-bold text-muted">
                            {m.tagline}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Summary + Submit */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                      <div className="text-xs uppercase tracking-widest font-bold text-[#0B3D91]">
                        Récapitulatif
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                        <div>
                          <span className="text-muted">Cause :</span>{" "}
                          <span className="font-bold text-[#1C1C1C]">{cause}</span>
                        </div>
                        <div>
                          <span className="text-muted">Montant :</span>{" "}
                          <span className="font-bold text-[#1C1C1C]">${finalAmount() || "—"}</span>
                        </div>
                        <div>
                          <span className="text-muted">Paiement :</span>{" "}
                          <span className="font-bold text-[#1C1C1C]">{method}</span>
                        </div>
                      </div>
                      <div className="mt-4 flex items-start gap-2 text-xs text-muted">
                        <Shield size={14} className="mt-0.5 shrink-0 text-[#0B3D91]" />
                        <span>
                          Vos données sont confidentielles. Aucun paiement ne sera effectué depuis ce site —
                          notre équipe vous accompagnera pas à pas sur WhatsApp.
                        </span>
                      </div>
                    </div>
                    <div className="lg:col-span-5 flex justify-end">
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="donate-submit"
                        className="w-full lg:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0B3D91] text-white font-bold rounded-full shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submitting ? "Redirection..." : "Faire un don"}
                        <MessageCircle size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {/* Trust footer */}
              <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-muted">
                <div className="flex items-center gap-2">
                  <Shield size={16} className="text-[#0B3D91]" />
                  <span>Transparence & intégrité — chaque don est utilisé avec responsabilité.</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle size={16} className="text-[#0B3D91]" />
                  <span>WhatsApp direct : {CONTACT.whatsapp}</span>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Donation;
